import Model, { attr, belongsTo, hasMany } from "@ember-data/model";
import { assert, warn } from "@ember/debug";
import { service } from "@ember/service";
import { isTesting } from "@embroider/macros";
import { EmberChangeset } from "ember-changeset";
import lookupValidator from "ember-changeset-validations";
import type IntlService from "ember-intl/services/intl";
import type Creator from "parog-web/models/creator";
import type FileAttachment from "parog-web/models/file-attachment";
import type Patient from "parog-web/models/patient";
import type FetcherService from "parog-web/services/fetcher";
import formatToISO8601Date from "parog-web/utils/format-to-iso8601-date";
import { sortBy } from "parog-web/utils/sort";
import type AlertTriggeredJournalEvent from "./alert-triggered-journal-event";
import type CommentWrittenJournalEvent from "./comment-written-journal-event";
import type NotificationTriggeredJournalEvent from "./notification-triggered-journal-event";

export interface Summary {
  type: string;
  description: string | undefined;
  date: string | undefined;
  je: JournalEventModel;
  forcIdentifier?: {
    feature: string;
    component: string;
    sourceJeId?: string | number;
  };
}

export default class JournalEventModel extends Model {
  static type = "journal-event";
  static validations = {};

  @service declare fetcher: FetcherService;
  @service declare intl: IntlService;

  @hasMany("alert-triggered-journal-event", {
    async: false,
    inverse: "triggeringJournalEvent",
    as: "journal-event"
  })
  declare alertTriggeredJournalEvents: AlertTriggeredJournalEvent[];
  @attr("string") declare comments: string;
  @hasMany("comment-written-journal-event", {
    async: false,
    inverse: "originJournalEvent",
    as: "journal-event"
  })
  declare commentWrittenJournalEvents: CommentWrittenJournalEvent[];
  @attr("date") declare createdAt: Date;
  @belongsTo("creator", { async: false, inverse: null, polymorphic: true })
  declare creator: Creator;
  @hasMany("file-attachment", {
    async: false,
    inverse: "journalEvent",
    as: "journal-event"
  })
  declare fileAttachments: FileAttachment[];
  @attr("file-attachments")
  declare fileAttachmentsAttributes;
  @attr("boolean", {
    defaultValue() {
      return false;
    }
  })
  declare isArchived: boolean;
  @hasMany("notification-triggered-journal-event", {
    async: false,
    inverse: "triggeringJournalEvent",
    as: "journal-event"
  })
  declare notificationTriggeredJournalEvents: NotificationTriggeredJournalEvent[];
  @attr("date-with-offset") declare occurredAt: Date;
  @attr("number") declare occurredAtOffsetInSeconds: number;
  @belongsTo("journal-event", {
    async: false,
    polymorphic: true,
    inverse: "triggeredJournalEvents",
    as: "journal-event"
  })
  declare originJournalEvent: JournalEventModel;
  @belongsTo("patient", {
    async: false,
    inverse: "journalEvents",
    as: "journal-event"
  })
  declare patient: Patient;
  @hasMany("journal-event", {
    async: false,
    polymorphic: true,
    inverse: "originJournalEvent",
    as: "journal-event"
  })
  declare triggeredJournalEvents: JournalEventModel[];
  @attr("date") declare updatedAt: Date;

  get description(): string | undefined {
    return this.intl.t(
      `journal-events.${(this.constructor as typeof JournalEventModel).type}`
    );
  }

  /**
   * This is an abstract property used by the timeline
   * to hide some JEs dynamically at run time.
   */
  get hideOnTimeline(): boolean {
    return false;
  }

  /**
   * Is there at least one alert triggered by this JE
   */
  get isAlertingJournalEvent(): boolean {
    return this.alertTriggeredJournalEvents.length > 0;
  }

  get latestFileAttachment(): FileAttachment | undefined {
    return this.orderedFileAttachments.at(-1);
  }

  get occurredOn(): Date {
    return new Date(
      this.occurredAt.getFullYear(),
      this.occurredAt.getMonth(),
      this.occurredAt.getDate()
    );
  }

  get orderedFileAttachments(): FileAttachment[] {
    return this.fileAttachments.toSorted(sortBy("index"));
  }

  get summary(): Summary {
    return {
      type: (this.constructor as typeof JournalEventModel).type,
      /** The description to display in the timeline */
      description: this.description,
      /** The date to display. Can be undefined if no date should be displayed. */
      date: this.occurredAt ? this.occurredAt.toISOString() : undefined,
      /** Give access to the JE in the timeline */
      je: this
    };
  }

  static patientJournalEventsRequest({
    fetcher,
    patientId,
    jeTypesToFetch,
    jeTypesToIgnore,
    occurredAfter,
    occurredBefore,
    useQuery
  }: {
    fetcher: FetcherService;
    patientId: string;
    jeTypesToFetch?: string[];
    jeTypesToIgnore?: string[];
    occurredAfter?: Date;
    occurredBefore?: Date;
    useQuery?: boolean;
  }): Promise<Array<JournalEventModel>> {
    const filter = { patient_id: patientId };
    if (jeTypesToFetch) {
      filter["type"] = jeTypesToFetch;
    }

    if (jeTypesToIgnore) {
      filter["not_of_type"] = jeTypesToIgnore;
    }

    if (occurredAfter) {
      filter["occurred_at_gte"] = formatToISO8601Date(occurredAfter);
    }

    if (occurredBefore) {
      filter["occurred_at_lte"] = formatToISO8601Date(occurredBefore);
    }

    const method = useQuery ? "query" : "loadRecords";

    return fetcher[method]("journal-event", {
      filter,
      include: "creator"
    }) as Promise<JournalEventModel[]>;
  }

  async archive(editedJournalEvent?: JournalEventModel): Promise<void> {
    assert("Store is not set on the model.", Boolean(this.store));
    assert("To be archived, the JE needs an ID.", !this.isNew);
    assert("You can't archive a JE already archived", !this.isArchived);

    if (this.isArchived) {
      throw `The archived journal event '${this.id}' cannot be archived again.`;
    }

    if (!isTesting()) {
      warn(
        "JE has unsaved properties, might be lost when archived.",
        Boolean(this.hasDirtyAttributes),
        { id: "parog-web.journal-event.archiving-dirty" }
      );
    }

    this.isArchived = true;

    if (editedJournalEvent) {
      await editedJournalEvent.save();
    }

    await this.save();

    this.triggeredJournalEvents.forEach(je => {
      this.store.unloadRecord(je);
    });

    // Reload journal events for the patient as new JeArchiveJEs may have been
    // created in the back (see Archivable#save! in the back).
    if (this.patient) {
      await JournalEventModel.patientJournalEventsRequest({
        fetcher: this.fetcher,
        patientId: this.patient.get("id")
      });
    }
  }

  createChangeset(): EmberChangeset {
    const validations = (this.constructor as typeof JournalEventModel)
      .validations;
    return new EmberChangeset(this, lookupValidator(validations), validations);
  }
}

declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "journal-event": JournalEventModel;
  }
}
